import type { ObligationFamily } from "../../academics-obligations/src/index.js";
import type { FinalizedPayment } from "../../payments-instruments/src/index.js";

export const documentsModule = "documents" as const;

export type FinancialDocumentKind = "cash-receipt" | "pending-acknowledgement";

export interface IssuanceAllocationSnapshot {
  readonly obligationId: string;
  readonly family: ObligationFamily;
  readonly period: string;
  readonly labelAr: string;
  readonly labelFr: string;
  readonly amountMad: string;
  readonly remainingAfterMad?: string;
}

export interface IssuanceSnapshot {
  readonly id: string;
  readonly version: "phase-02-v1";
  readonly kind: FinancialDocumentKind;
  readonly paymentId: string;
  readonly reference: string;
  readonly issuedAt: string;
  readonly receivedDate: string;
  readonly student: {
    readonly nameAr: string;
    readonly nameFr: string;
    readonly classLabel: string;
  };
  readonly amountMad: string;
  readonly method: FinalizedPayment["method"];
  readonly allocations: readonly IssuanceAllocationSnapshot[];
  readonly placeholderSchoolIdentity: true;
  readonly immutable: true;
}

export function createIssuanceSnapshot(input: {
  readonly id: string;
  readonly payment: FinalizedPayment;
  readonly issuedAt: string;
  readonly student: IssuanceSnapshot["student"];
  readonly allocations: readonly IssuanceAllocationSnapshot[];
}): IssuanceSnapshot {
  return {
    id: input.id,
    version: "phase-02-v1",
    kind: input.payment.method.method === "cash" ? "cash-receipt" : "pending-acknowledgement",
    paymentId: input.payment.id,
    reference: input.payment.reference,
    issuedAt: input.issuedAt,
    receivedDate: input.payment.receivedDate,
    student: { ...input.student },
    amountMad: input.payment.amountMad.toString(),
    method: { ...input.payment.method },
    allocations: input.allocations.map((allocation) => ({ ...allocation })),
    placeholderSchoolIdentity: true,
    immutable: true,
  };
}

export function semanticDocumentFields(snapshot: IssuanceSnapshot) {
  return {
    identity: snapshot.id,
    kind: snapshot.kind,
    reference: snapshot.reference,
    issuedAt: snapshot.issuedAt,
    receivedDate: snapshot.receivedDate,
    student: snapshot.student,
    amountMad: snapshot.amountMad,
    method: snapshot.method,
    allocations: snapshot.allocations,
    lifecycle: snapshot.kind === "cash-receipt" ? "settled" : "pending",
  } as const;
}
